import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw, ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { Field, SelectShell, inputStyles, selectStyles } from "../../components/crm/formControls";
import toast from "react-hot-toast";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { dcPost, dcGet } from "../../services/districtCourtApi";

import SelectMenu from "../../components/crm/SelectMenu";
const STATES = [
  { code: "28", name: "Andaman and Nicobar" },
  { code: "2", name: "Andhra Pradesh" },
  { code: "36", name: "Arunachal Pradesh" },
  { code: "6", name: "Assam" },
  { code: "8", name: "Bihar" },
  { code: "27", name: "Chandigarh" },
  { code: "18", name: "Chhattisgarh" },
  { code: "26", name: "Delhi" },
  { code: "30", name: "Goa" },
  { code: "17", name: "Gujarat" },
  { code: "14", name: "Haryana" },
  { code: "5", name: "Himachal Pradesh" },
  { code: "12", name: "Jammu and Kashmir" },
  { code: "7", name: "Jharkhand" },
  { code: "3", name: "Karnataka" },
  { code: "4", name: "Kerala" },
  { code: "33", name: "Ladakh" },
  { code: "37", name: "Lakshadweep" },
  { code: "23", name: "Madhya Pradesh" },
  { code: "1", name: "Maharashtra" },
  { code: "25", name: "Manipur" },
  { code: "21", name: "Meghalaya" },
  { code: "19", name: "Mizoram" },
  { code: "34", name: "Nagaland" },
  { code: "11", name: "Odisha" },
  { code: "35", name: "Puducherry" },
  { code: "22", name: "Punjab" },
  { code: "9", name: "Rajasthan" },
  { code: "24", name: "Sikkim" },
  { code: "10", name: "Tamil Nadu" },
  { code: "29", name: "Telangana" },
  { code: "38", name: "The Dadra And Nagar Haveli And Daman And Diu" },
  { code: "20", name: "Tripura" },
  { code: "15", name: "Uttarakhand" },
  { code: "13", name: "Uttar Pradesh" },
  { code: "16", name: "West Bengal" },
];

const SEARCH_TABS = [
  { id: "party-name", label: "Party Name" },
  { id: "case-number", label: "Case Number" },
  { id: "filing-number", label: "Filing Number" },
  { id: "advocate-name", label: "Advocate Name" },
  { id: "cnr-number", label: "CNR Number" },
  { id: "case-type", label: "Case Type" },
];

// Top-level search categories (mirrors the High Court flow): the existing case
// status search vs. the new court-orders search. Each category has its own tabs.
const SEARCH_CATEGORIES = [
  { id: "case-status", label: "Case Status" },
  { id: "court-orders", label: "Court Orders" },
];

const COURT_ORDER_TABS = [
  { id: "co-party-name", label: "Party Name" },
  { id: "co-case-number", label: "Case Number" },
  { id: "co-order-date", label: "Order Date" },
];

// Order-type ("frad") choices on the eCourts court-order page.
// Values must match eCourts' radio values exactly (verified against the live
// site): interim / finalorder / both. ("final" returns no results.)
const ORDER_TYPES = [
  { value: "both", label: "Both" },
  { value: "interim", label: "Interim" },
  { value: "finalorder", label: "Final" },
];

const SEARCH_TIPS = [
  "Select State, District & Court Complex first",
  "Refresh the captcha if it's unclear",
  "Captcha is case sensitive",
];

function FetchFromDistrictCourt() {
  const navigate = useNavigate();

  // Session & location state
  const [sessionId, setSessionId] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [districts, setDistricts] = useState([]);
  const [distCode, setDistCode] = useState("");
  const [courts, setCourts] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState(null); // full court object
  const [showEstablishments, setShowEstablishments] = useState(false);
  const [establishments, setEstablishments] = useState([]);
  const [estCode, setEstCode] = useState("");
  const [caseTypes, setCaseTypes] = useState([]);

  // Captcha & UI state
  const [captchaImage, setCaptchaImage] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [searchCategory, setSearchCategory] = useState("case-status");
  const [activeTab, setActiveTab] = useState("party-name");
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [error, setError] = useState("");
  const [fetchLoading, setFetchLoading] = useState({
    districts: false,
    courts: false,
    establishments: false,
    caseTypes: false,
  });

  // Year options
  const yearOptions = Array.from({ length: 76 }, (_, i) => String(new Date().getFullYear() - i));

  // Separate form state per tab
  const [partyForm, setPartyForm] = useState({
    petitionerName: "",
    registrationYear: new Date().getFullYear().toString(),
    status: "Both",
    captcha: "",
  });
  const [caseNumberForm, setCaseNumberForm] = useState({
    caseType: "",
    caseNumber: "",
    registrationYear: new Date().getFullYear().toString(),
    captcha: "",
  });
  const [filingForm, setFilingForm] = useState({
    filingNumber: "",
    year: new Date().getFullYear().toString(),
    captcha: "",
  });
  const [advocateForm, setAdvocateForm] = useState({
    advocateName: "",
    status: "Both",
    captcha: "",
  });
  const [cnrForm, setCnrForm] = useState({
    cino: "",
    captcha: "",
  });
  const [caseTypeForm, setCaseTypeForm] = useState({
    caseType: "",
    year: new Date().getFullYear().toString(),
    status: "Pending",
    captcha: "",
  });

  // Court-orders form state (mirrors the High Court court-order sub-searches)
  const [coPartyForm, setCoPartyForm] = useState({
    partyName: "",
    registrationYear: new Date().getFullYear().toString(),
    orderType: "both",
    captcha: "",
  });
  const [coCaseNumberForm, setCoCaseNumberForm] = useState({
    caseType: "",
    caseNumber: "",
    registrationYear: new Date().getFullYear().toString(),
    orderType: "both",
    captcha: "",
  });
  const [coOrderDateForm, setCoOrderDateForm] = useState({
    fromDate: "",
    toDate: "",
    orderType: "both",
    captcha: "",
  });

  // Clear localStorage on mount
  useEffect(() => {
    localStorage.removeItem("dc_search_payload");
    localStorage.removeItem("dc_case_details_payload");
    localStorage.removeItem("dc_districts_payload");
  }, []);

  // Helper to unwrap API response — backend wraps data in { success, result: {...} }
  const unwrap = (data) => {
    if (data?.result && typeof data.result === "object") return data.result;
    return data;
  };

  // Initialize session
  useEffect(() => {
    const initSession = async () => {
      setInitLoading(true);
      setError("");
      try {
        const res = await dcPost("/api/common/init", {});
        if (!res.ok) throw new Error(`Init HTTP error! status: ${res.status}`);
        const data = unwrap(res.body);
        setSessionId(data.sessionId || "");
      } catch (err) {
        console.error("Init Error:", err);
        setError(`Failed to initialize session: ${err.message}`);
      } finally {
        setInitLoading(false);
      }
    };
    initSession();
  }, []);

  // Fetch districts when state changes
  const fetchDistricts = useCallback(async (sCode, sid) => {
    if (!sCode || !sid) return;
    setFetchLoading((prev) => ({ ...prev, districts: true }));
    setError("");
    setDistricts([]);
    setCourts([]);
    setEstablishments([]);
    setCaseTypes([]);
    setSelectedCourt(null);
    setDistCode("");
    setEstCode("");
    setCaptchaImage("");
    try {
      const response = await dcPost("/api/common/districts", { sessionId: sid, stateCode: sCode });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = unwrap(response.body);
      setDistricts(data.districts || []);
    } catch (err) {
      console.error("Fetch Districts Error:", err);
      setError(`Failed to fetch districts: ${err.message}`);
    } finally {
      setFetchLoading((prev) => ({ ...prev, districts: false }));
    }
  }, []);

  // Fetch courts when district changes
  const fetchCourts = useCallback(async (sCode, dCode, sid) => {
    if (!sCode || !dCode || !sid) return;
    setFetchLoading((prev) => ({ ...prev, courts: true }));
    setError("");
    setCourts([]);
    setEstablishments([]);
    setCaseTypes([]);
    setSelectedCourt(null);
    setEstCode("");
    setCaptchaImage("");
    try {
      const response = await dcPost("/api/common/courts", { sessionId: sid, stateCode: sCode, distCode: dCode });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = unwrap(response.body);
      setCourts(data.courts || []);
    } catch (err) {
      console.error("Fetch Courts Error:", err);
      setError(`Failed to fetch courts: ${err.message}`);
    } finally {
      setFetchLoading((prev) => ({ ...prev, courts: false }));
    }
  }, []);

  // Fetch establishments when needed
  const fetchEstablishments = useCallback(async (sCode, dCode, complexCode, sid) => {
    if (!sCode || !dCode || !complexCode || !sid) return;
    setFetchLoading((prev) => ({ ...prev, establishments: true }));
    setError("");
    setEstablishments([]);
    setEstCode("");
    try {
      const response = await dcPost("/api/common/establishments", {
        sessionId: sid,
        stateCode: sCode,
        distCode: dCode,
        courtComplexCode: complexCode,
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = unwrap(response.body);
      setEstablishments(data.establishments || []);
    } catch (err) {
      console.error("Fetch Establishments Error:", err);
      setError(`Failed to fetch establishments: ${err.message}`);
    } finally {
      setFetchLoading((prev) => ({ ...prev, establishments: false }));
    }
  }, []);

  // The court-orders flow has its own captcha (the eCourts courtorder/index page),
  // distinct from the case-status search captcha.
  const captchaEndpoint =
    searchCategory === "court-orders"
      ? "/api/courtorders/captcha"
      : "/api/common/captcha";

  // Set fields and fetch captcha
  const setFieldsAndFetchCaptcha = useCallback(async (sid, sCode, dCode, complexCodeFull, estCodeVal) => {
    if (!sid || !sCode || !dCode || !complexCodeFull) return;
    setError("");
    setCaptchaLoading(true);
    try {
      // Set fields first
      await dcPost("/api/common/set-fields", {
        sessionId: sid,
        complexCode: complexCodeFull,
        stateCode: sCode,
        distCode: dCode,
        estCode: estCodeVal || "",
      });

      // Then fetch captcha
      const captchaRes = await dcGet(captchaEndpoint, { sessionId: sid });
      if (!captchaRes.ok) throw new Error(`Captcha HTTP error! status: ${captchaRes.status}`);
      const captchaData = unwrap(captchaRes.body);
      const captchaImg = captchaData.captcha || captchaData.captchaImage || captchaData.captchaImageBase64;
      if (captchaImg) {
        setCaptchaImage(captchaImg);
      } else {
        setError("Captcha image not found in response.");
      }
    } catch (err) {
      console.error("Set Fields / Captcha Error:", err);
      setError(`Failed to set fields or fetch captcha: ${err.message}`);
    } finally {
      setCaptchaLoading(false);
    }
  }, [captchaEndpoint]);

  // Fetch captcha only (for refresh / tab change)
  const fetchCaptcha = useCallback(async () => {
    if (!sessionId) return;
    setCaptchaLoading(true);
    setError("");
    try {
      const response = await dcGet(captchaEndpoint, { sessionId });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = unwrap(response.body);
      const captchaImg = data.captcha || data.captchaImage || data.captchaImageBase64;
      if (captchaImg) {
        setCaptchaImage(captchaImg);
      } else {
        setError("Captcha image not found in response.");
      }
    } catch (err) {
      console.error("Fetch Captcha Error:", err);
      setError(`Failed to fetch captcha: ${err.message}`);
    } finally {
      setCaptchaLoading(false);
    }
  }, [sessionId, captchaEndpoint]);

  // Fetch case types for case-number and case-type tabs
  const fetchCaseTypes = useCallback(async (tab) => {
    if (!sessionId) return;
    setFetchLoading((prev) => ({ ...prev, caseTypes: true }));
    try {
      let endpoint;
      let body;

      if (tab === "case-number" || tab === "co-case-number") {
        const complexCode = selectedCourt?.complex_number?.split("@")[0] || "";
        endpoint = "/api/casenumber/case-types";
        body = {
          sessionId,
          stateCode,
          distCode,
          courtComplexCode: complexCode,
          estCode: estCode || "",
          searchType: "c_no",
        };
      } else {
        endpoint = "/api/common/case-types";
        body = { sessionId };
      }

      const response = await dcPost(endpoint, body);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = unwrap(response.body);
      setCaseTypes(data.case_types || data.caseTypes || []);
    } catch (err) {
      console.warn("Failed to fetch case types:", err);
    } finally {
      setFetchLoading((prev) => ({ ...prev, caseTypes: false }));
    }
  }, [sessionId, selectedCourt, stateCode, distCode, estCode]);

  // Effects: state change → fetch districts
  useEffect(() => {
    if (stateCode && sessionId) {
      fetchDistricts(stateCode, sessionId);
    }
  }, [stateCode, sessionId, fetchDistricts]);

  // Effects: district change → fetch courts
  useEffect(() => {
    if (distCode && stateCode && sessionId) {
      fetchCourts(stateCode, distCode, sessionId);
    }
  }, [distCode, stateCode, sessionId, fetchCourts]);

  // Effects: court selection → fetch establishments if needed, set fields, fetch captcha
  useEffect(() => {
    if (!selectedCourt || !sessionId) return;

    // complex_number already contains the full value e.g. "1260008@5,6,7@N"
    const complexCodeFull = selectedCourt.complex_number;
    // Extract just the numeric code for establishments API (e.g. "1260008")
    const complexCodeOnly = complexCodeFull.split("@")[0];

    if (selectedCourt.differ_mast_est === "Y") {
      setShowEstablishments(true);
      fetchEstablishments(stateCode, distCode, complexCodeOnly, sessionId);
      // Don't set fields yet — wait for establishment selection
    } else {
      setShowEstablishments(false);
      setEstCode("");
      setFieldsAndFetchCaptcha(sessionId, stateCode, distCode, complexCodeFull, "");
    }
  }, [selectedCourt, sessionId, stateCode, distCode, fetchEstablishments, setFieldsAndFetchCaptcha]);

  // Effects: establishment selection → set fields and fetch captcha
  useEffect(() => {
    if (!showEstablishments || !estCode || !selectedCourt || !sessionId) return;
    const complexCodeFull = selectedCourt.complex_number;
    setFieldsAndFetchCaptcha(sessionId, stateCode, distCode, complexCodeFull, estCode);
  }, [estCode, showEstablishments, selectedCourt, sessionId, stateCode, distCode, setFieldsAndFetchCaptcha]);

  // Effects: fetch case types when relevant tab is active and captcha is loaded
  useEffect(() => {
    if (
      (activeTab === "case-number" ||
        activeTab === "case-type" ||
        activeTab === "co-case-number") &&
      captchaImage &&
      sessionId
    ) {
      fetchCaseTypes(activeTab);
    }
  }, [activeTab, captchaImage, sessionId, fetchCaseTypes]);

  // Effects: clear captcha input and refresh captcha on tab change
  useEffect(() => {
    if (captchaImage && sessionId) {
      fetchCaptcha();
    }
    setError("");
    // Clear captcha for the active tab
    switch (activeTab) {
      case "party-name":
        setPartyForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "case-number":
        setCaseNumberForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "filing-number":
        setFilingForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "advocate-name":
        setAdvocateForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "cnr-number":
        setCnrForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "case-type":
        setCaseTypeForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "co-party-name":
        setCoPartyForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "co-case-number":
        setCoCaseNumberForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "co-order-date":
        setCoOrderDateForm((prev) => ({ ...prev, captcha: "" }));
        break;
      default:
        break;
    }
  }, [activeTab, searchCategory]);

  // Handlers
  const handleStateChange = (e) => {
    setStateCode(e.target.value);
    setDistCode("");
    setCourts([]);
    setSelectedCourt(null);
    setEstablishments([]);
    setEstCode("");
    setCaptchaImage("");
    setCaseTypes([]);
  };

  const handleDistrictChange = (e) => {
    setDistCode(e.target.value);
    setSelectedCourt(null);
    setEstablishments([]);
    setEstCode("");
    setCaptchaImage("");
    setCaseTypes([]);
  };

  const handleCourtChange = (e) => {
    const courtIdx = e.target.value;
    if (courtIdx === "") {
      setSelectedCourt(null);
      return;
    }
    const court = courts[parseInt(courtIdx, 10)];
    setSelectedCourt(court || null);
    setEstCode("");
    setCaptchaImage("");
    setCaseTypes([]);
  };

  const handleEstablishmentChange = (e) => {
    setEstCode(e.target.value);
  };

  // Switch between the "Case Status" and "Court Orders" search categories. Each
  // category resets to its first tab; the tab-change effect refreshes the
  // captcha against the category-appropriate endpoint.
  const handleCategoryChange = (category) => {
    if (category === searchCategory) return;
    setSearchCategory(category);
    setError("");
    setActiveTab(category === "court-orders" ? "co-party-name" : "party-name");
  };

  // Search handler
  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      let requestData;
      let endpoint;
      let searchBy;

      // Court context for court-order searches. complex_number looks like
      // "1260008@5,6,7@N": [0] is the numeric complex code, [1] the est-code
      // array. The backend resolves these from the session too, but we pass them
      // explicitly so the search is independent of session-field timing. We send
      // both `courtComplexCode` (party-name route) and `courtComplex`
      // (case-no / order-date routes) since the routes read different keys.
      const courtContext = () => {
        const parts = selectedCourt?.complex_number?.split("@") || [];
        const complexCodeOnly = parts[0] || "";
        const complexArr = parts[1] || "";
        return {
          stateCode,
          distCode,
          courtComplexCode: complexCodeOnly,
          courtComplex: complexCodeOnly,
          courtComplexArr: complexArr,
          // eCourts expects est_code="null" to search ALL establishments in a
          // complex (verified against the live submitPartyName request). When a
          // specific establishment is picked, `estCode` holds its real code.
          // Defaulting to "1" broke complexes whose establishments aren't 1
          // (e.g. Delhi → Rouse Avenue = 5,6,7) — eCourts returned an empty body.
          estCode: estCode || "null",
        };
      };

      switch (activeTab) {
        case "party-name":
          requestData = {
            sessionId,
            petresName: partyForm.petitionerName,
            rgyearP: partyForm.registrationYear,
            caseStatus: partyForm.status,
            captchaCode: partyForm.captcha,
          };
          endpoint = "/api/partyname/case-data";
          searchBy = "CSpartyName";
          break;

        case "case-number":
          requestData = {
            sessionId,
            caseType: caseNumberForm.caseType,
            caseNo: caseNumberForm.caseNumber,
            rgyear: caseNumberForm.registrationYear,
            captchaCode: caseNumberForm.captcha,
          };
          endpoint = "/api/casenumber/case-data";
          searchBy = "CScaseNumber";
          break;

        case "filing-number":
          requestData = {
            sessionId,
            filingNo: filingForm.filingNumber,
            filyear: filingForm.year,
            captchaCode: filingForm.captcha,
          };
          endpoint = "/api/filingnumber/case-data";
          searchBy = "CSfilingNumber";
          break;

        case "advocate-name": {
          const complexCode = selectedCourt?.complex_number?.split("@")[0] || "";
          requestData = {
            sessionId,
            advocateName: advocateForm.advocateName,
            caseStatus: advocateForm.status,
            captchaCode: advocateForm.captcha,
            stateCode,
            distCode,
            courtComplexCode: complexCode,
            estCode: estCode || "",
          };
          endpoint = "/api/advocatename/case-data";
          searchBy = "CSAdvName";
          break;
        }

        case "cnr-number":
          requestData = {
            sessionId,
            cino: cnrForm.cino,
            captchaCode: cnrForm.captcha,
          };
          endpoint = "/api/cnr/search";
          searchBy = "CNRNumber";
          break;

        case "case-type": {
          const complexCodeCT = selectedCourt?.complex_number?.split("@")[0] || "";
          requestData = {
            sessionId,
            case_type_1: caseTypeForm.caseType,
            search_year: caseTypeForm.year,
            case_status: caseTypeForm.status,
            ct_captcha_code: caseTypeForm.captcha,
            stateCode,
            distCode,
            courtComplexCode: complexCodeCT,
            estCode: estCode || "",
          };
          endpoint = "/api/casetype/case-data";
          searchBy = "CScaseType";
          break;
        }

        case "co-party-name": {
          requestData = {
            sessionId,
            partynameOrder: coPartyForm.partyName,
            rgyearOrder: coPartyForm.registrationYear,
            frad: coPartyForm.orderType,
            captchaCode: coPartyForm.captcha,
            ...courtContext(),
          };
          endpoint = "/api/courtorders/party-name";
          searchBy = "COpartyName";
          break;
        }

        case "co-case-number": {
          requestData = {
            sessionId,
            caseType: coCaseNumberForm.caseType,
            caseNo: coCaseNumberForm.caseNumber,
            year: coCaseNumberForm.registrationYear,
            frad: coCaseNumberForm.orderType,
            captchaCode: coCaseNumberForm.captcha,
            ...courtContext(),
          };
          endpoint = "/api/courtorders/case-no/search";
          searchBy = "COcaseNumber";
          break;
        }

        case "co-order-date": {
          // HTML date inputs are YYYY-MM-DD; eCourts expects DD-MM-YYYY.
          const toDMY = (d) => (d ? d.split("-").reverse().join("-") : "");
          requestData = {
            sessionId,
            fromDate: toDMY(coOrderDateForm.fromDate),
            toDate: toDMY(coOrderDateForm.toDate),
            frad: coOrderDateForm.orderType,
            captchaCode: coOrderDateForm.captcha,
            ...courtContext(),
          };
          endpoint = "/api/courtorders/order-date/search";
          searchBy = "COorderDate";
          break;
        }

        default:
          return;
      }

      const response = await dcPost(endpoint, requestData);
      const rawData = response.body;
      const data = unwrap(rawData);

      // Check for failure (captcha error, session expired, etc.)
      if (rawData?.success === false || data?.error) {
        const errMsg = rawData?.error || data?.error || rawData?.message || "Search failed";
        toast.error(errMsg, { duration: 5000 });
        setError(errMsg);

        // If API returns a new captcha, use it directly
        const nextCaptcha = rawData?.nextCaptcha?.captcha || data?.nextCaptcha?.captcha;
        if (nextCaptcha) {
          setCaptchaImage(nextCaptcha);
        } else {
          fetchCaptcha();
        }

        // Clear captcha input for active tab
        switch (activeTab) {
          case "party-name":
            setPartyForm((prev) => ({ ...prev, captcha: "" }));
            break;
          case "case-number":
            setCaseNumberForm((prev) => ({ ...prev, captcha: "" }));
            break;
          case "filing-number":
            setFilingForm((prev) => ({ ...prev, captcha: "" }));
            break;
          case "advocate-name":
            setAdvocateForm((prev) => ({ ...prev, captcha: "" }));
            break;
          case "cnr-number":
            setCnrForm((prev) => ({ ...prev, captcha: "" }));
            break;
          case "case-type":
            setCaseTypeForm((prev) => ({ ...prev, captcha: "" }));
            break;
          case "co-party-name":
            setCoPartyForm((prev) => ({ ...prev, captcha: "" }));
            break;
          case "co-case-number":
            setCoCaseNumberForm((prev) => ({ ...prev, captcha: "" }));
            break;
          case "co-order-date":
            setCoOrderDateForm((prev) => ({ ...prev, captcha: "" }));
            break;
          default:
            break;
        }
        return;
      }

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      // Store search payload
      localStorage.setItem("dc_search_payload", JSON.stringify(requestData));

      // Court Orders — results are order records; navigate to the orders screen.
      // `data` is the unwrapped result: { metadata, courtBreakdown, orders, ... }
      if (searchCategory === "court-orders") {
        const orders = data?.orders || [];
        if (!orders.length) {
          setError("No court orders found for this search.");
          const nextCaptcha = rawData?.nextCaptcha?.captcha || data?.nextCaptcha?.captcha;
          if (nextCaptcha) setCaptchaImage(nextCaptcha);
          else fetchCaptcha();
          return;
        }
        navigate("/portal/districtCourtOrders", {
          state: {
            data,
            searchParams: requestData,
            sessionId,
            stateCode,
            distCode,
            complexCode: selectedCourt?.complex_number?.split("@")[0] || "",
            searchBy,
          },
        });
        return;
      }

      // CNR returns case details directly — navigate to case details page
      if (activeTab === "cnr-number") {
        const caseDetails = data?.caseDetails || data?.data;
        if (!caseDetails) {
          setError("No case details found for this CNR number");
          fetchCaptcha();
          return;
        }
        navigate("/portal/districtCourtCaseDetails", {
          state: {
            caseDetails,
            stateCode,
            distCode,
            complexCode: selectedCourt?.complex_number?.split("@")[0] || "",
            sessionId,
            searchBy,
            status: "pending",
          },
        });
        return;
      }

      // All other tabs — navigate to case list
      const searchParams = {
        ...requestData,
        status: requestData.caseStatus || "Pending",
        stateCode,
        distCode,
        complexCode: selectedCourt?.complex_number?.split("@")[0] || "",
        searchBy,
        activeTab,
      };

      navigate("/portal/districtCourtList", {
        state: {
          data,
          searchParams,
          sessionId,
          stateCode,
          distCode,
          complexCode: selectedCourt?.complex_number?.split("@")[0] || "",
          searchBy,
        },
      });
    } catch (err) {
      const errorMessage = err?.message || "Failed to search cases";
      setError(errorMessage);
      toast.error(errorMessage, { duration: 5000 });
      fetchCaptcha();
    } finally {
      setLoading(false);
    }
  };

  // Reset handler
  const handleReset = () => {
    const defaultYear = new Date().getFullYear().toString();
    switch (activeTab) {
      case "party-name":
        setPartyForm({ petitionerName: "", registrationYear: defaultYear, status: "Both", captcha: "" });
        break;
      case "case-number":
        setCaseNumberForm({ caseType: "", caseNumber: "", registrationYear: defaultYear, captcha: "" });
        break;
      case "filing-number":
        setFilingForm({ filingNumber: "", year: defaultYear, captcha: "" });
        break;
      case "advocate-name":
        setAdvocateForm({ advocateName: "", status: "Both", captcha: "" });
        break;
      case "cnr-number":
        setCnrForm({ cino: "", captcha: "" });
        break;
      case "case-type":
        setCaseTypeForm({ caseType: "", year: defaultYear, status: "Pending", captcha: "" });
        break;
      case "co-party-name":
        setCoPartyForm({ partyName: "", registrationYear: defaultYear, orderType: "both", captcha: "" });
        break;
      case "co-case-number":
        setCoCaseNumberForm({ caseType: "", caseNumber: "", registrationYear: defaultYear, orderType: "both", captcha: "" });
        break;
      case "co-order-date":
        setCoOrderDateForm({ fromDate: "", toDate: "", orderType: "both", captcha: "" });
        break;
      default:
        break;
    }
    if (captchaImage) fetchCaptcha();
  };

  // Reusable order-type radio for the court-order sub-forms.
  const renderOrderTypeRadio = (value, onChange) => (
    <div className="flex h-10 flex-wrap items-center gap-4">
      {ORDER_TYPES.map((opt) => (
        <label key={opt.value} className="flex cursor-pointer items-center gap-1.5 text-sm text-slate-700">
          <input
            type="radio"
            name="orderTypeRadio"
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            className="accent-yellow-500"
          />
          {opt.label}
        </label>
      ))}
    </div>
  );

  // Reusable status radio buttons
  const renderStatusRadio = (value, onChange, options = ["Pending", "Disposed", "Both"]) => (
    <div className="flex h-10 flex-wrap items-center gap-4">
      {options.map((opt) => (
        <label key={opt} className="flex cursor-pointer items-center gap-1.5 text-sm text-slate-700">
          <input
            type="radio"
            name="statusRadio"
            value={opt}
            checked={value === opt}
            onChange={() => onChange(opt)}
            className="accent-yellow-500"
          />
          {opt}
        </label>
      ))}
    </div>
  );

  // Shared year select (defaults never empty, so validity is always satisfied).
  const renderYearSelect = (value, onChange) => (
    <SelectShell>
      <SelectMenu chevron={false} value={value} onChange={(e) => onChange(e.target.value)} className={selectStyles} required>
        {yearOptions.map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </SelectMenu>
    </SelectShell>
  );

  // Shared case-type select — populated asynchronously; shows a busy chevron.
  const renderCaseTypeSelect = (value, onChange) => (
    <SelectShell busy={fetchLoading.caseTypes}>
      <SelectMenu chevron={false}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={selectStyles}
        disabled={fetchLoading.caseTypes}
        required
      >
        <option value="" disabled>
          {fetchLoading.caseTypes ? "Loading..." : "Select Case Type"}
        </option>
        {caseTypes.map((type) => (
          <option key={type.code || type.value} value={type.code || type.value}>
            {type.options || type.name}
          </option>
        ))}
      </SelectMenu>
    </SelectShell>
  );

  // Tab-specific fields — each returns Field cells laid out by the parent grid.
  const renderSearchFields = () => {
    switch (activeTab) {
      case "party-name":
        return (
          <>
            <Field label="Petitioner/Respondent Name">
              <input
                type="text"
                value={partyForm.petitionerName}
                onChange={(e) => setPartyForm({ ...partyForm, petitionerName: e.target.value })}
                placeholder="Name"
                className={inputStyles}
                required
              />
            </Field>
            <Field label="Registration Year">
              {renderYearSelect(partyForm.registrationYear, (val) => setPartyForm({ ...partyForm, registrationYear: val }))}
            </Field>
            <Field label="Status">
              {renderStatusRadio(partyForm.status, (val) => setPartyForm({ ...partyForm, status: val }))}
            </Field>
          </>
        );
      case "case-number":
        return (
          <>
            <Field label="Case Type">
              {renderCaseTypeSelect(caseNumberForm.caseType, (val) => setCaseNumberForm({ ...caseNumberForm, caseType: val }))}
            </Field>
            <Field label="Case Number">
              <input
                type="text"
                value={caseNumberForm.caseNumber}
                onChange={(e) => setCaseNumberForm({ ...caseNumberForm, caseNumber: e.target.value })}
                placeholder="Case Number"
                className={inputStyles}
                required
              />
            </Field>
            <Field label="Year">
              {renderYearSelect(caseNumberForm.registrationYear, (val) => setCaseNumberForm({ ...caseNumberForm, registrationYear: val }))}
            </Field>
          </>
        );
      case "filing-number":
        return (
          <>
            <Field label="Filing Number" className="sm:col-span-2">
              <input
                type="text"
                value={filingForm.filingNumber}
                onChange={(e) => setFilingForm({ ...filingForm, filingNumber: e.target.value })}
                placeholder="Filing Number"
                className={inputStyles}
                required
              />
            </Field>
            <Field label="Year">
              {renderYearSelect(filingForm.year, (val) => setFilingForm({ ...filingForm, year: val }))}
            </Field>
          </>
        );
      case "advocate-name":
        return (
          <>
            <Field label="Advocate Name" className="sm:col-span-2">
              <input
                type="text"
                value={advocateForm.advocateName}
                onChange={(e) => setAdvocateForm({ ...advocateForm, advocateName: e.target.value })}
                placeholder="Advocate Name"
                className={inputStyles}
                required
              />
            </Field>
            <Field label="Status">
              {renderStatusRadio(advocateForm.status, (val) => setAdvocateForm({ ...advocateForm, status: val }))}
            </Field>
          </>
        );
      case "cnr-number":
        return (
          <Field label="CNR Number" className="sm:col-span-2">
            <input
              type="text"
              value={cnrForm.cino}
              onChange={(e) => setCnrForm({ ...cnrForm, cino: e.target.value })}
              placeholder="CNR Number"
              className={inputStyles}
              required
            />
          </Field>
        );
      case "case-type":
        return (
          <>
            <Field label="Case Type">
              {renderCaseTypeSelect(caseTypeForm.caseType, (val) => setCaseTypeForm({ ...caseTypeForm, caseType: val }))}
            </Field>
            <Field label="Year">
              {renderYearSelect(caseTypeForm.year, (val) => setCaseTypeForm({ ...caseTypeForm, year: val }))}
            </Field>
            <Field label="Status">
              {renderStatusRadio(caseTypeForm.status, (val) => setCaseTypeForm({ ...caseTypeForm, status: val }), ["Pending", "Disposed"])}
            </Field>
          </>
        );
      case "co-party-name":
        return (
          <>
            <Field label="Petitioner/Respondent Name">
              <input
                type="text"
                value={coPartyForm.partyName}
                onChange={(e) => setCoPartyForm({ ...coPartyForm, partyName: e.target.value })}
                placeholder="Party Name"
                className={inputStyles}
                required
              />
            </Field>
            <Field label="Registration Year" required={false}>
              {renderYearSelect(coPartyForm.registrationYear, (val) => setCoPartyForm({ ...coPartyForm, registrationYear: val }))}
            </Field>
            <Field label="Order Type">
              {renderOrderTypeRadio(coPartyForm.orderType, (val) => setCoPartyForm({ ...coPartyForm, orderType: val }))}
            </Field>
          </>
        );
      case "co-case-number":
        return (
          <>
            <Field label="Case Type">
              {renderCaseTypeSelect(coCaseNumberForm.caseType, (val) => setCoCaseNumberForm({ ...coCaseNumberForm, caseType: val }))}
            </Field>
            <Field label="Case Number">
              <input
                type="text"
                value={coCaseNumberForm.caseNumber}
                onChange={(e) => setCoCaseNumberForm({ ...coCaseNumberForm, caseNumber: e.target.value })}
                placeholder="Case Number"
                className={inputStyles}
                required
              />
            </Field>
            <Field label="Year">
              {renderYearSelect(coCaseNumberForm.registrationYear, (val) => setCoCaseNumberForm({ ...coCaseNumberForm, registrationYear: val }))}
            </Field>
            <Field label="Order Type">
              {renderOrderTypeRadio(coCaseNumberForm.orderType, (val) => setCoCaseNumberForm({ ...coCaseNumberForm, orderType: val }))}
            </Field>
          </>
        );
      case "co-order-date":
        return (
          <>
            <Field label="From Date">
              <input
                type="date"
                value={coOrderDateForm.fromDate}
                onChange={(e) => setCoOrderDateForm({ ...coOrderDateForm, fromDate: e.target.value })}
                className={inputStyles}
                required
              />
            </Field>
            <Field label="To Date">
              <input
                type="date"
                value={coOrderDateForm.toDate}
                onChange={(e) => setCoOrderDateForm({ ...coOrderDateForm, toDate: e.target.value })}
                className={inputStyles}
                required
              />
            </Field>
            <Field label="Order Type">
              {renderOrderTypeRadio(coOrderDateForm.orderType, (val) => setCoOrderDateForm({ ...coOrderDateForm, orderType: val }))}
            </Field>
          </>
        );
      default:
        return null;
    }
  };

  // Get captcha value and setter for active tab
  const getCaptchaProps = () => {
    switch (activeTab) {
      case "party-name":
        return { value: partyForm.captcha, onChange: (e) => setPartyForm({ ...partyForm, captcha: e.target.value }) };
      case "case-number":
        return { value: caseNumberForm.captcha, onChange: (e) => setCaseNumberForm({ ...caseNumberForm, captcha: e.target.value }) };
      case "filing-number":
        return { value: filingForm.captcha, onChange: (e) => setFilingForm({ ...filingForm, captcha: e.target.value }) };
      case "advocate-name":
        return { value: advocateForm.captcha, onChange: (e) => setAdvocateForm({ ...advocateForm, captcha: e.target.value }) };
      case "cnr-number":
        return { value: cnrForm.captcha, onChange: (e) => setCnrForm({ ...cnrForm, captcha: e.target.value }) };
      case "case-type":
        return { value: caseTypeForm.captcha, onChange: (e) => setCaseTypeForm({ ...caseTypeForm, captcha: e.target.value }) };
      case "co-party-name":
        return { value: coPartyForm.captcha, onChange: (e) => setCoPartyForm({ ...coPartyForm, captcha: e.target.value }) };
      case "co-case-number":
        return { value: coCaseNumberForm.captcha, onChange: (e) => setCoCaseNumberForm({ ...coCaseNumberForm, captcha: e.target.value }) };
      case "co-order-date":
        return { value: coOrderDateForm.captcha, onChange: (e) => setCoOrderDateForm({ ...coOrderDateForm, captcha: e.target.value }) };
      default:
        return { value: "", onChange: () => {} };
    }
  };

  const currentTabs = searchCategory === "court-orders" ? COURT_ORDER_TABS : SEARCH_TABS;
  const activeTabLabel = currentTabs.find((t) => t.id === activeTab)?.label || "";
  const categoryLabel = searchCategory === "court-orders" ? "Court Orders" : "Case Status";
  const captchaProps = getCaptchaProps();
  const isCaptchaReady = !!captchaImage;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm">
          <Link to="/portal/findCasesOptions" className="text-slate-500 hover:text-slate-800">
            Find Cases
          </Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">District Court</span>
        </div>

        {/* Stepper */}
        <div className="mt-4">
          <FindCaseStepper current={2} />
        </div>

        {/* Form Card — full width */}
        <div className="mt-4 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {initLoading ? (
            <div className="p-8 text-center sm:p-12">
              <SkeletonTheme baseColor="var(--skeleton-base)" highlightColor="var(--skeleton-highlight)">
                <Skeleton height={20} width="60%" className="mb-3 mx-auto" />
                <Skeleton height={16} width="40%" className="mb-2 mx-auto" />
                <Skeleton height={16} width="50%" className="mb-2 mx-auto" />
                <Skeleton height={12} width="35%" className="mb-2 mx-auto" />
              </SkeletonTheme>
              <p className="mt-6 animate-pulse text-sm text-yellow-500">Initializing session...</p>
            </div>
          ) : (
            <>
              {/* State / District / Court Complex / Establishment */}
              <div className="grid grid-cols-1 gap-x-6 gap-y-4 border-b border-slate-100 p-5 sm:p-6 md:grid-cols-3">
                <Field label="State">
                  <SelectShell>
                    <SelectMenu chevron={false} value={stateCode} onChange={handleStateChange} className={selectStyles} required>
                      <option value="" disabled>Choose State</option>
                      {STATES.map((state) => (
                        <option key={state.code} value={state.code}>{state.name}</option>
                      ))}
                    </SelectMenu>
                  </SelectShell>
                </Field>

                <Field label="District">
                  <SelectShell busy={fetchLoading.districts}>
                    <SelectMenu chevron={false}
                      value={distCode}
                      onChange={handleDistrictChange}
                      className={selectStyles}
                      disabled={!stateCode || fetchLoading.districts}
                      required
                    >
                      <option value="" disabled>
                        {fetchLoading.districts
                          ? "Loading..."
                          : stateCode
                            ? "Choose District"
                            : "Select State First"}
                      </option>
                      {districts.map((d) => (
                        <option key={d.code} value={d.code}>{d.options || d.name}</option>
                      ))}
                    </SelectMenu>
                  </SelectShell>
                </Field>

                <Field label="Court Complex">
                  <SelectShell busy={fetchLoading.courts}>
                    <SelectMenu chevron={false}
                      value={selectedCourt ? courts.indexOf(selectedCourt).toString() : ""}
                      onChange={handleCourtChange}
                      className={selectStyles}
                      disabled={!distCode || fetchLoading.courts}
                      required
                    >
                      <option value="" disabled>
                        {fetchLoading.courts
                          ? "Loading..."
                          : distCode
                            ? "Choose Court Complex"
                            : "Select District First"}
                      </option>
                      {courts.map((court, idx) => (
                        <option key={idx} value={idx.toString()}>{court.options || court.name}</option>
                      ))}
                    </SelectMenu>
                  </SelectShell>
                </Field>

                {showEstablishments && (
                  <Field label="Court Establishment">
                    <SelectShell busy={fetchLoading.establishments}>
                      <SelectMenu chevron={false}
                        value={estCode}
                        onChange={handleEstablishmentChange}
                        className={selectStyles}
                        disabled={fetchLoading.establishments}
                        required
                      >
                        <option value="" disabled>
                          {fetchLoading.establishments ? "Loading..." : "Choose Establishment"}
                        </option>
                        {establishments.map((est) => (
                          <option key={est.code} value={est.code}>{est.options || est.name}</option>
                        ))}
                      </SelectMenu>
                    </SelectShell>
                  </Field>
                )}
              </div>

              <form onSubmit={handleSearch}>
                <div className="p-5 sm:p-6">
                  {/* Category — Case Status vs Court Orders */}
                  <div className="flex justify-center">
                    <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 p-1">
                      {SEARCH_CATEGORIES.map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => handleCategoryChange(cat.id)}
                          className={`rounded-full px-6 py-1.5 text-sm font-semibold transition-all duration-200 ${
                            searchCategory === cat.id
                              ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200"
                              : "text-slate-500 hover:text-slate-800"
                          }`}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Search By pills */}
                  <p className="mb-2 mt-5 text-center text-xs font-medium text-slate-600">Search By</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {currentTabs.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={`rounded-full px-4 py-1.5 text-[13px] font-medium transition-all duration-200 ${
                          activeTab === tab.id
                            ? "bg-yellow-500 text-slate-900 shadow-sm"
                            : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  <h2 className="mt-6 text-sm font-semibold text-slate-800">
                    {categoryLabel} : Search by {activeTabLabel}
                  </h2>

                  {error && (
                    <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 ring-1 ring-red-100">{error}</p>
                  )}

                  {/* Fields + captcha share one grid so they align across the full width */}
                  <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
                    {renderSearchFields()}

                    <Field label="Captcha" className="lg:col-start-1">
                      <div className="flex items-stretch gap-2">
                        <div className="flex h-10 flex-1 items-center justify-center rounded-lg border border-slate-300 bg-slate-50">
                          {captchaLoading ? (
                            <span className="flex items-center gap-2 text-sm text-slate-500">
                              <RefreshCw className="h-4 w-4 animate-spin" />
                              Loading...
                            </span>
                          ) : captchaImage ? (
                            <img
                              src={captchaImage}
                              alt="CAPTCHA"
                              className="max-h-[34px] object-contain"
                              onError={() => setError("Failed to load captcha image.")}
                            />
                          ) : (
                            <span className="px-2 text-center text-xs text-slate-400">Select Court to load Captcha</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={fetchCaptcha}
                          disabled={!isCaptchaReady}
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-500 transition-colors hover:border-yellow-400 hover:text-yellow-600 disabled:opacity-50 disabled:hover:border-slate-300 disabled:hover:text-slate-500"
                          title="Refresh Captcha"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                      </div>
                    </Field>

                    <Field label="Enter Captcha">
                      <input
                        type="text"
                        value={captchaProps.value}
                        onChange={captchaProps.onChange}
                        placeholder="Enter Captcha"
                        className={inputStyles}
                        required
                      />
                    </Field>
                  </div>
                </div>

                {/* Footer — tips + actions */}
                <div className="flex flex-col gap-4 border-t border-slate-100 bg-slate-50/60 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                  <ul className="flex flex-wrap items-center gap-x-5 gap-y-1">
                    {SEARCH_TIPS.map((tip) => (
                      <li key={tip} className="flex items-center gap-1.5 text-xs text-slate-500">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-yellow-400" />
                        {tip}
                      </li>
                    ))}
                  </ul>
                  <div className="flex shrink-0 gap-3">
                    <button
                      type="button"
                      onClick={handleReset}
                      className="h-10 rounded-lg border border-slate-300 bg-white px-6 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60"
                      disabled={loading}
                    >
                      Reset
                    </button>
                    <button
                      type="submit"
                      className="h-10 rounded-lg bg-yellow-500 px-8 text-sm font-semibold text-slate-900 transition-colors hover:bg-yellow-400 disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={loading || captchaLoading || !isCaptchaReady}
                    >
                      {loading ? "Searching..." : "Search Cases"}
                    </button>
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default FetchFromDistrictCourt;
